import {
  ChartDataType,
  DataKeysType,
  ReportType,
  UrlObject,
} from "features/Reports";

import i18n from "i18n";
import { getFirstName, sortChartData } from "./Common";

export interface IProgressEntity {
  assignedLos: number;
  avgCompletedLos: number;
}

export interface IProgressDataEntity extends IProgressEntity {
  xAxisLabel: string;
  title: string;
  selectedItem?: string;
}
export interface ITeacherProgressDataEntity extends IProgressEntity {
  teacherCode: string;
  teacherName: string;
}

export interface IClassProgressDataEntity extends IProgressEntity {
  classCode: string;
  classTitle: string;
  gradeCode: string;
  gradeTitle: string;
}

export interface IStudentProgressDataEntity extends IProgressEntity {
  studentName: string;
  studentCode: string;
}

export interface IGradeProgressDataEntity extends IProgressEntity {
  gradeCode: string;
}

export const translateProgressData = (
  data: IProgressDataEntity[],
  reportType: ReportType
): ChartDataType[] => {
  let result: ChartDataType[] = [];
  if (data.length) {
    data.forEach((item) => {
      const row = {
        [reportType === ReportType.Class_Report
          ? i18n.t("Number Uncompleted Lessons")
          : i18n.t("Assigned Lessons")]: item.assignedLos ?? 0,
        [reportType === ReportType.Class_Report
          ? i18n.t("Number Completed Lessons")
          : i18n.t("Completed Lessons")]: item.avgCompletedLos ?? 0,
        xAxisLabel: i18n.t(item.xAxisLabel),
        title: i18n.t(item.title),
        selectedItem: item.selectedItem,
      } as ChartDataType;

      result.push(row);
    });
  }
  return result;
};

export const getProgressDataKeys = (
  reportType: ReportType,
  t: (value: string) => string
): DataKeysType[] => {
  return reportType === ReportType.Class_Report
    ? [
        {
          label: t("Number Completed Lessons"),
          color: "rgba(26, 67, 135, 1)",
        },
        {
          label: t("Number Uncompleted Lessons"),
          color: "rgba(162, 208, 249, 1)",
        },
      ]
    : [
        { label: t("Completed Lessons"), color: "rgba(26, 67, 135, 1)" },
        {
          label: t("Assigned Lessons"),
          color: "rgba(162, 208, 249, 1)",
        },
      ];
};

export const transformTeachersProgressData = (
  data: ITeacherProgressDataEntity[]
): IProgressDataEntity[] => {
  return data
    .map((item) => ({
      xAxisLabel: getFirstName(item.teacherName),
      title: item.teacherName,
      assignedLos: item.assignedLos,
      avgCompletedLos: item.avgCompletedLos,
      selectedItem:
        item.assignedLos || item.avgCompletedLos ? item.teacherCode : undefined,
    }))
    .sort(sortChartData);
};

export const transformTeacherProgressData = (
  data: IClassProgressDataEntity[]
): IProgressDataEntity[] => {
  return data
    .map((item) => ({
      xAxisLabel: item.classTitle,
      title: `${item.classTitle} - ${item.gradeTitle}`,
      assignedLos: item.assignedLos,
      avgCompletedLos: item.avgCompletedLos,
      selectedItem:
        item.assignedLos || item.avgCompletedLos ? item.classCode : undefined,
    }))
    .sort(sortChartData);
};

export const transformGradesProgressData = (
  data: IGradeProgressDataEntity[]
): IProgressDataEntity[] => {
  return data
    .map((item: any) => ({
      xAxisLabel: item.gradeCode,
      title: item.gradeCode,
      assignedLos: item.assignedLos,
      avgCompletedLos: item.avgCompletedLos,
      selectedItem:
        item.assignedLos || item.avgCompletedLos ? item.gradeCode : null,
    }))
    .sort(sortChartData);
};

export const transformGradeProgressData = (
  data: IClassProgressDataEntity[]
): IProgressDataEntity[] => {
  return data
    .map((item: any) => ({
      xAxisLabel: item.classTitle,
      title: item.classTitle,
      assignedLos: item.assignedLos,
      avgCompletedLos: item.avgCompletedLos,
      selectedItem:
        item.assignedLos || item.avgCompletedLos ? item.classCode : null,
    }))
    .sort(sortChartData);
};

export const transformClassProgressReport = (
  data: IStudentProgressDataEntity[]
): IProgressDataEntity[] => {
  return data
    .map((item: any) => ({
      xAxisLabel: getFirstName(item.studentName),
      title: item.studentName,
      assignedLos: item.assignedLos,
      avgCompletedLos: item.avgCompletedLos,
    }))
    .sort(sortChartData);
};

export const logAnalyticsEvent = (
  logger: (event: string, info?: { [key: string]: string }) => void,
  label: string,
  reportType: ReportType,
  url?: string
) => {
  if (url === UrlObject.GradesUrl)
    logger("ClickOnGradesAssignReportFilter", {
      Duration: label,
      Source: reportType,
    });
  else
    logger("ClickOnTeachersAssignReportFilter", {
      Duration: label,
      Source: reportType,
    });
};

export const getLabelAndTitle = (name: string) => {
  return {
    xAxisLabel: getFirstName(name),
    title: name,
  };
};
